import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { uniqueWords, convertGenderText } from '../utils/consts';
import {
    Box,
    Typography,
    FormControl,
    FormLabel,
    FormGroup,
    FormControlLabel,
    Checkbox,
    // FormHelperText,
    Button,
    // Stepper,
    // Step,
    // StepLabel,
    List,
    ListItem,
    ListItemText,
    IconButton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const ValuesSection = ({ onNext, onBack, isFirstSection, formData }) => {
    const [step, setStep] = useState(() => {
        // If all data is complete (has hierarchia_wartosci), show first step for review
        if (formData?.hierarchia_wartosci && formData.hierarchia_wartosci.length > 0) {
            return 0;
        }
        // If user has already started elimination, show elimination step
        if (formData?.wartosci_wybrane_poczatek && formData.wartosci_wybrane_poczatek.length > 0) {
            return 1;
        }
        return 0;
    });
    const [selectedValues, setSelectedValues] = useState(() => {
        // Combine hierarchia_wartosci and wartosci_wybrane_poczatek to get initial selected values
        if (formData?.hierarchia_wartosci || formData?.wartosci_wybrane_poczatek) {
            return [...(formData.wartosci_wybrane_poczatek || []), ...(formData.hierarchia_wartosci || [])];
        }
        return [];
    });
    const [eliminatedValues, setEliminatedValues] = useState(formData?.wartosci_wybrane_poczatek || []);
    const [valuesList, setValuesList] = useState([]);
    const { handleSubmit } = useForm({
        defaultValues: {
            wartosci_dodatkowe_mysli: formData?.wartosci_dodatkowe_mysli || ''
        }
    });

    // Update selected and eliminated values when formData changes
    useEffect(() => {
        if (formData) {
            if (formData?.hierarchia_wartosci || formData?.wartosci_wybrane_poczatek) {
                setSelectedValues([...(formData.wartosci_wybrane_poczatek || []), ...(formData.hierarchia_wartosci || [])]);
                setEliminatedValues(formData?.wartosci_wybrane_poczatek || []);
            }
        }
    }, [formData]);

    useEffect(() => {
        fetch('/assets/lista_wartosci.txt')
            .then(response => response.text())
            .then(text => {
                const values = text.split('\n').filter(value => value.trim());
                setValuesList(values);
            })
            .catch(error => {
                console.error('Error loading values list:', error);
                // Fallback to hardcoded values if file loading fails
                setValuesList(uniqueWords);
            });
    }, []);

    // Funkcja zwracająca pytanie eliminacyjne na podstawie aktualnego stanu
    const getEliminationQuestion = () => {
        const eliminatedCount = eliminatedValues.length;
        const remainingCount = selectedValues.length;

        // Pierwsza rezygnacja
        if (eliminatedCount === 0) {
            return `Gdybyś ${convertGenderText('musiał/a', formData.zwrot_plec)} zrezygnować z jednej z wybranych wartości, która by to była?`;
        }
        // Druga rezygnacja
        if (eliminatedCount === 1) {
            return `Z której z pozostałych wartości najłatwiej byłoby Ci zrezygnować?`;
        }
        // Trzecia rezygnacja
        if (eliminatedCount === 2) {
            return `Która z pozostałych wartości jest dla Ciebie najmniej istotna?`;
        }
        // Ostatnia rezygnacja (gdy zostały 2 wartości)
        if (remainingCount === 2) {
            return `Z której z tych dwóch wartości, łatwiej byłoby Ci zrezygnować?`;
        }
        // Domyślne pytanie dla pozostałych etapów
        return `Która z pozostałych wartości jest dla Ciebie najmniej istotna?`;
    };

    const handleValueSelection = (value) => {
        if (selectedValues.includes(value)) {
            setSelectedValues(selectedValues.filter(v => v !== value));
        } else if (selectedValues.length < 5) {
            setSelectedValues([...selectedValues, value]);
        }
    };

    const handleValueElimination = (value) => {
        if (eliminatedValues.length < 9) {
            const newEliminatedValues = [...eliminatedValues, value];
            const newSelectedValues = selectedValues.filter(v => v !== value);

            setEliminatedValues(newEliminatedValues);
            setSelectedValues(newSelectedValues);

            // Auto-submit when only one value remains
            if (newSelectedValues.length === 1) {
                // Use setTimeout to ensure state updates are processed
                setTimeout(() => {
                    const finalData = {
                        wartosci_wybrane_poczatek: newEliminatedValues,
                        hierarchia_wartosci: newSelectedValues,
                    };
                    onNext(finalData);
                }, 0);
            }
        }
    };

    const handleFinalSubmit = (data) => {
        const finalData = {
            wartosci_wybrane_poczatek: eliminatedValues,
            hierarchia_wartosci: selectedValues.filter(v => !eliminatedValues.includes(v)),
            ...data
        };
        onNext(finalData);
    };

    const renderStep = () => {
        switch (step) {
            case 0:
                return (
                    <>
                        <Typography variant="body1" sx={{ mb: 3 }} component="div">
                            <b>Co to są wartości?</b>
                            <br />
                            Wartości to nasze osobiste przekonania o tym, co w naszym życiu jest naprawdę ważne, cenne i warte wysiłku. To one wskazują nam kierunek, pomagają podejmować decyzje i nadają sens codziennym działaniom.
                            <br /><br />
                            Na nasze wartości wpływają doświadczenia, wychowanie, kultura i środowisko, w którym żyjemy. To właśnie wartości wyznaczają kierunek, który prowadzi nas do poczucia sensu, szczęścia i napędza do działania.
                        </Typography>
                        <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
                            <FormLabel component="legend">
                                <Typography variant="h6" color="black">
                                    Wybierz 5 wartości, które są dla Ciebie najważniejsze:
                                </Typography>
                            </FormLabel>
                            <FormGroup
                                style={{
                                    'display': 'grid',
                                    'gridTemplateColumns': 'repeat(2, 1fr)'
                                }}>
                                {(selectedValues.length === 5 ? selectedValues : valuesList).map((value) => (
                                    <FormControlLabel
                                        key={value}
                                        control={
                                            <Checkbox
                                                checked={selectedValues.includes(value)}
                                                onChange={() => handleValueSelection(value)}
                                            />
                                        }
                                        label={value}
                                    />
                                ))}
                            </FormGroup>
                        </FormControl>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <IconButton
                                onClick={onBack}
                                disabled={isFirstSection}
                                sx={{
                                    border: '1px solid',
                                    borderColor: isFirstSection ? 'action.disabled' : 'primary.main',
                                    borderRadius: 1
                                }}
                            >
                                <ArrowBackIcon />
                            </IconButton>
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                disabled={selectedValues.length !== 5}
                                onClick={() => {
                                    setEliminatedValues([]);
                                    setStep(1)
                                }}
                                endIcon={<ArrowForwardIcon />}
                            >
                                Dalej
                            </Button>
                        </Box>
                    </>
                );
            case 1:
                return (
                    <Box component="form" onSubmit={handleSubmit(handleFinalSubmit)} >
                        <Typography variant="h6" color="black" gutterBottom>
                            {getEliminationQuestion()}
                        </Typography>
                        <List >
                            {selectedValues.map((value) => (
                                <ListItem
                                    key={value}
                                    button
                                    onClick={() => handleValueElimination(value)}
                                >
                                    <ListItemText primary={value} />
                                </ListItem>
                            ))}
                        </List>
                    </Box >
                );
            // case 2:
            //     return (
            //         <Box component="form" onSubmit={handleSubmit(handleFinalSubmit)}>
            //             <Typography variant="h6" gutterBottom>
            //                 Twoja najważniejsza wartość:
            //             </Typography>
            //             <Typography variant="body1" gutterBottom sx={{ mb: 3 }}>
            //                 {selectedValues[0]}
            //             </Typography>
            //             <Controller
            //                 name="wartosci_dodatkowe_mysli"
            //                 control={control}
            //                 render={({ field }) => (
            //                     <TextField
            //                         {...field}
            //                         fullWidth
            //                         multiline
            //                         rows={4}
            //                         label="Dodatkowe przemyślenia dotyczące Twoich wartości (opcjonalne)"
            //                         sx={{ mb: 3 }}
            //                     />
            //                 )}
            //             />
            // <Button
            //     type="submit"
            //     variant="contained"
            //     color="primary"
            //     fullWidth
            // >
            //     Dalej
            // </Button>
            //         </Box>
            //     );
            default:
                return null;
        }
    };

    return (
        <Box sx={{ mt: 3 }}>
            {/* <Stepper activeStep={step} sx={{ mb: 4 }}>
                <Step>
                    <StepLabel>Wybór wartości</StepLabel>
                </Step>
                <Step>
                    <StepLabel>Eliminacja</StepLabel>
                </Step>
                <Step>
                    <StepLabel>Podsumowanie</StepLabel>
                </Step>
            </Stepper> */}
            {renderStep()}
        </Box>
    );
};

export default ValuesSection; 